import { useState } from 'react';
import { Database, FileCode2, SquareCode } from 'lucide-react';
import { toast } from 'sonner';
import { useSuspenseQuery, useMutation } from '@apollo/client/react';

import { AssetContentTypeEnum, ContentTypeEnum } from 'src/graphql/schema';
import { AssetsUpdateDocument } from 'src/graphql/actions/asset/assetsUpdate.mutation';
import { SoftwareEscrowDocument } from 'src/graphql/actions/softwareEscrow/softwareEscrow.query';
import { SoftwareEscrowUpdateContentTypesDocument } from 'src/graphql/actions/softwareEscrow/softwareEscrowUpdateContentTypes.mutation';
import { ClientError, getApiError } from 'src/utils/errors';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ToggleGroupCard, ToggleGroupCardItem } from '@/components/ui/toggle-group';

type Props = {
  assetIds: string[];
  refetch: () => void;
  children: React.ReactNode;
  onClose?: () => void;
  escrowId: string;
};

const options = [
  {
    name: 'Source code',
    description:
      'The core source code, including all scripts, libraries, and dependencies required to build and run the application.',
    value: AssetContentTypeEnum.SourceCode,
    icon: SquareCode,
  },
  {
    name: 'Documentation',
    description:
      'Technical and operational guides, including API references, setup instructions, system architecture, and any relevant notes.',
    value: AssetContentTypeEnum.Documentation,
    icon: FileCode2,
  },
  {
    name: 'Data',
    description:
      "Essential datasets, configurations, and database exports necessary for the application's functionality.",
    value: AssetContentTypeEnum.Data,
    icon: Database,
  },
];

const AssignAssetTypeDialog = ({ assetIds, refetch, children, onClose, escrowId }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [assetContentType, setAssetContentType] = useState<AssetContentTypeEnum | null>(null);
  const { data: escrowData } = useSuspenseQuery(SoftwareEscrowDocument, {
    variables: {
      escrowId,
    },
  });
  const [updateSoftwareEscrow, { loading: loadingSoftwareEscrow }] = useMutation(
    SoftwareEscrowUpdateContentTypesDocument,
  );

  const isMarkedAsRequired =
    assetContentType && (escrowData.softwareEscrow.requiredContentTypes as string[]).includes(assetContentType);

  const onMarkRequired = async () => {
    if (isMarkedAsRequired) return;

    const newRequiredContentTypes = [
      ...(escrowData.softwareEscrow.requiredContentTypes as string[]),
      assetContentType,
    ] as ContentTypeEnum[];

    await updateSoftwareEscrow({
      variables: {
        softwareEscrowId: escrowId,
        requiredContentTypes: newRequiredContentTypes,
      },
    });
  };

  const [updateAssets, { loading }] = useMutation(AssetsUpdateDocument, {
    onCompleted: refetch,
  });
  const isValid = assetIds.length && assetContentType !== null;

  const handleAssign = async () => {
    if (!isValid) return;

    try {
      // First mark the asset as required
      await onMarkRequired();

      // Then update the assets
      const { data } = await updateAssets({
        variables: {
          assetIds,
          assetParams: {
            contentType: assetContentType,
          },
        },
      });

      if (data?.assetsUpdate?.success) {
        toast.success(assetIds.length > 1 ? 'Assets updated' : 'Asset updated', {
          description: `The asset type has been assigned to the selected ${assetIds.length > 1 ? 'assets' : 'asset'}.`,
        });
        setIsOpen(false);
        onClose?.();
        setAssetContentType(null);
      } else {
        throw new ClientError(data?.assetsUpdate?.errors?.[0]);
      }
    } catch (error) {
      getApiError(error, toast.error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-[600px] gap-8">
        <DialogHeader className="gap-1.5">
          <DialogTitle className="text-xl font-bold">Label asset</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Choose which asset type this asset should be labeled as.
          </DialogDescription>
        </DialogHeader>
        <ToggleGroupCard
          type="single"
          className="space-y-4"
          value={assetContentType ?? ''}
          onValueChange={(value) => setAssetContentType(value ? (value as AssetContentTypeEnum) : null)}
        >
          {options.map(({ value, icon, name, description }) => (
            <ToggleGroupCardItem key={value} value={value} icon={icon} title={name} description={description} />
          ))}
        </ToggleGroupCard>
        <DialogFooter className="sm:justify-between">
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!isValid || loadingSoftwareEscrow || loading}
            isLoading={loading || loadingSoftwareEscrow}
            onClick={handleAssign}
          >
            Label
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssignAssetTypeDialog;
